import { useCallback, useMemo, useState } from 'react';
import type { FieldMapping, MappingResponse } from '../../services/api';
import { collectMappingSourceFieldNames } from '../graph/transformAnalysis';
import type {
  MappingTableFilter,
  MappingTableRowData,
  MappingTableSort,
} from './types';

export interface UseMappingTableStateOptions {
  mappingResponse?: MappingResponse | null;
}

/**
 * State hook for the mapping table view — derives rows from MappingResponse,
 * handles sort, filter, search, and multi-selection.
 */
export function useMappingTableState({ mappingResponse }: UseMappingTableStateOptions) {
  const [sort, setSort] = useState<MappingTableSort>({ field: 'target', direction: 'asc' });
  const [filter, setFilter] = useState<MappingTableFilter>({ type: 'all' });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Derive all rows from mappings (mapped) + schema unmapped fields
  const allRows: MappingTableRowData[] = useMemo(() => {
    if (!mappingResponse || !Array.isArray(mappingResponse.mappings)) return [];

    const rows: MappingTableRowData[] = [];

    // 1. Build rows from actual mappings
    const mappedTargetFields = new Set<string>();
    mappingResponse.mappings.forEach((m: FieldMapping) => {
      const key = `${m.target_table}.${m.target_field}`;
      mappedTargetFields.add(key.toLowerCase());

      const steps = m.transformation_steps ?? [];
      const expression = steps.map(s => s.expression || s.operation || '').filter(Boolean).join(' → ');
      const aiGenerated = steps.some(s => s.ai_generated);

      const recipeApproved = mappingResponse.status === 'approved';
      const fieldApproved = Boolean(m.approved_at);
      const sourceFields = collectMappingSourceFieldNames(m);
      rows.push({
        id: m.mapping_id,
        sourceTable: m.source_file || '',
        sourceField: sourceFields[0] || m.source_field || '',
        sourceFields: sourceFields.length > 0 ? sourceFields : undefined,
        targetTable: m.target_table || '',
        targetField: m.target_field || '',
        transformation: expression || m.transformation || '',
        transformationType: m.transformation_type || 'direct',
        operation: m.operation,
        parameters: m.parameters,
        confidence: m.confidence ?? 0,
        isMapped: true,
        aiGenerated,
        approvalStatus:
          m.field_status === 'inherited' ? 'inherited' :
          m.field_status === 'broken' ? 'broken' :
          m.field_status === 'stale' ? 'stale' :
          // `user_authored` wins over `approved` for chip display — it's
          // a strictly more informative label (operator overrode AI).
          m.field_status === 'user_authored' ? 'user_authored' :
          recipeApproved || fieldApproved ? 'approved' : 'pending',
        mapping: m,
      });
    });

    // 2. Build rows from target schema for unmapped fields
    const targetSnapshot =
      mappingResponse.transformation_plan?.target_schema_snapshot ??
      mappingResponse.metadata?.target_schema_snapshot ??
      mappingResponse.metadata?.target_schema_snapshot;

    if (targetSnapshot?.tables) {
      Object.entries(targetSnapshot.tables).forEach(([tableName, tableData]) => {
        const rawCols = tableData?.columns;
        const columns: Array<Record<string, unknown>> = Array.isArray(rawCols)
          ? rawCols
          : rawCols && typeof rawCols === 'object'
            ? Object.entries(rawCols as Record<string, unknown>).map(([name, def]) => ({ name, ...(typeof def === 'object' && def !== null ? def as Record<string, unknown> : {}) }))
            : [];
        columns.forEach((col) => {
          const colName = typeof col === 'string' ? col : (col?.name as string) || (col?.column_name as string) || '';
          if (!colName) return;
          const key = `${tableName}.${colName}`.toLowerCase();
          if (!mappedTargetFields.has(key)) {
            rows.push({
              id: `unmapped:${tableName}.${colName}`,
              sourceTable: '',
              sourceField: '',
              targetTable: tableName,
              targetField: colName,
              transformation: '',
              transformationType: '',
              confidence: 0,
              isMapped: false,
              aiGenerated: false,
              approvalStatus: 'missing',
            });
          }
        });
      });
    }

    return rows;
  }, [mappingResponse]);

  // Apply filter
  const filteredRows = useMemo(() => {
    let result = allRows;

    // Type filter
    switch (filter.type) {
      case 'mapped':
        result = result.filter(r => r.isMapped);
        break;
      case 'unmapped':
        result = result.filter(r => !r.isMapped);
        break;
      case 'low-confidence':
        result = result.filter(r => r.isMapped && r.confidence < 0.7);
        break;
      case 'errors':
        result = result.filter(r => r.approvalStatus === 'error' || (!r.isMapped));
        break;
    }

    // Search query
    if (filter.searchQuery) {
      const q = filter.searchQuery.toLowerCase();
      result = result.filter(
        r =>
          r.sourceField.toLowerCase().includes(q) ||
          (r.sourceFields ?? []).some((field) => field.toLowerCase().includes(q)) ||
          r.targetField.toLowerCase().includes(q) ||
          r.sourceTable.toLowerCase().includes(q) ||
          r.targetTable.toLowerCase().includes(q) ||
          r.transformation.toLowerCase().includes(q)
      );
    }

    return result;
  }, [allRows, filter]);

  // Apply sort
  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    const dir = sort.direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      switch (sort.field) {
        case 'source':
          return dir * `${a.sourceTable}.${(a.sourceFields ?? [a.sourceField]).join(',')}`.localeCompare(
            `${b.sourceTable}.${(b.sourceFields ?? [b.sourceField]).join(',')}`,
          );
        case 'target':
          return dir * `${a.targetTable}.${a.targetField}`.localeCompare(`${b.targetTable}.${b.targetField}`);
        case 'transformation':
          return dir * a.transformation.localeCompare(b.transformation);
        case 'confidence':
          return dir * (a.confidence - b.confidence);
        case 'status':
          return dir * (Number(a.isMapped) - Number(b.isMapped));
        default:
          return 0;
      }
    });

    return sorted;
  }, [filteredRows, sort]);

  // Group by target table for section headers
  const groupedRows = useMemo(() => {
    const groups = new Map<string, MappingTableRowData[]>();
    sortedRows.forEach(row => {
      const key = row.targetTable || '(no table)';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row);
    });
    return groups;
  }, [sortedRows]);

  // Selection handlers
  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(sortedRows.map(r => r.id)));
  }, [sortedRows]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleToggleSort = useCallback((field: MappingTableSort['field']) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  return {
    allRows,
    filteredRows,
    sortedRows,
    groupedRows,
    sort,
    filter,
    setFilter,
    selectedIds,
    handleToggleSelect,
    handleSelectAll,
    handleDeselectAll,
    handleToggleSort,
  };
}
